/**
 * SelectionHull.ts
 * -----------------------------------------------------------------------------
 * A contour round each SELECTED bridge component, drawn as geometry rather than
 * as a post-process. Experimental alternative to the orange overlay mesh in
 * BridgeScene.select — see `selectionStyle` there. Neither this nor the overlay
 * touches the GeometryOutline edge lines, which stay exactly as they are.
 *
 * WHY NOT three.js's `OutlinePass` (webgl_postprocessing_outline)
 * That example renders the chosen objects into a one-bit mask, finds the edges
 * of the mask and blurs them. One bit is the problem: two selected components
 * touching on screen are one connected region with one silhouette, so what
 * comes back is a line round the PAIR rather than a line round each. Giving the
 * mask a per-object identity means replacing the mask material, the edge shader
 * AND the overlay — whose `maskColor.r` factor deliberately keeps the line
 * outside the silhouette and so erases the very component-to-component boundary
 * the identity was added to find. One pass per component is one pass per pier.
 *
 * It would also have cost us things this viewport already has: a composer's
 * buffer does not inherit the renderer's `antialias` (Viewport3D asks for MSAA,
 * and a bridge is nothing but thin diagonal edges), and three skips tone mapping
 * when it is not drawing to the screen, so ACESFilmic would need putting back by
 * hand in a trailing `OutputPass`. Here the frame is still drawn by the
 * viewport's own `renderer.render(scene, camera)` and the contour is just more
 * scene.
 *
 * WHAT THIS IS INSTEAD: an INVERTED HULL, which is per-component by
 * construction. Draw the component's triangles a second time, a little bigger,
 * back faces only. The real surface covers the hull everywhere except just
 * outside its silhouette, which leaves a rim — and because the rim is real
 * geometry at the component's own depth, a girder in front of a pier covers that
 * pier's contour exactly as it covers the pier.
 *
 * WHAT IT COSTS: one extra draw call per selected component and one extra
 * transform of its triangles. No composer, no render targets, no second copy of
 * the frame. Selections here are a handful of components, not the whole model,
 * which is what makes this affordable — hulling the merged `BridgeModel` mesh
 * would give one contour round the entire bridge, i.e. the OutlinePass failure
 * again.
 *
 * WIDTH IS IN CSS PIXELS, NOT WORLD UNITS. Under a perspective camera the hull
 * is pushed out in VIEW space by an amount proportional to its distance from the
 * camera, so the contour is the same weight on a near abutment and a far one.
 * Pushed out in world units instead, a 200 m deck viewed end-on would have a
 * contour that thins to nothing at exactly the distance a viewer most needs the
 * help. Under an orthographic camera on-screen size does not vary with depth, so
 * the push is a constant derived from the frustum height and zoom — the same
 * split Viewport3D.updateOrbitMarkerScale already makes.
 */

import * as THREE from 'three';
import type { Viewport3D } from './Viewport3D';

/**
 * How far out the hull goes, in CSS pixels, at `weight` 1.
 *
 * CSS rather than framebuffer pixels so the contour reads the same weight
 * whatever `setPixelRatio` the host picked (Viewport3D clamps it to 1.5).
 *
 * Set from looking rather than from arithmetic: under about 2 the line breaks up
 * under multisampling on distant spans, and over about 4 it stops reading as a
 * contour and starts reading as a mount round a photograph.
 */
const LINE_PX = 2.5;

/**
 * Default multiplier on {@link LINE_PX}, i.e. 1.25 CSS px out of the box.
 *
 * Half the base rather than the base itself because the contour does not draw
 * alone: OutlineCreases puts a 1-pixel line down every hard edge (WebGL ignores
 * `LineBasicMaterial.linewidth`, so a crease is one device pixel and cannot be
 * anything else). A 2.5 px contour beside a 1 px crease reads as two different
 * pens on the same drawing — the silhouette shouting over its own interior. At
 * half weight the two are close enough to read as one hand.
 */
const DEFAULT_WEIGHT = 0.5;

/** Quantisation for welding coincident vertices, in metres. Well below anything
 *  the engine authors (mesh vertices land on millimetres) and well above float
 *  noise on a 200 m model. */
const WELD = 1e-4;

/**
 * Smoothed normals for a non-indexed triangle soup.
 *
 * WHY SMOOTHED, NOT PER-FACE. A hull pushed along per-face normals comes apart
 * wherever the mesh has a hard edge: a rectangular column has three normals at
 * each corner, so its faces fly apart into detached quads and the contour is
 * drawn with a gap at every corner. Averaging the normals of every vertex that
 * shares a position welds the hull back into one closed surface. The corners
 * come out chamfered by about the line's own width, which at 2.5 px nobody sees.
 *
 * BridgeScene keeps `selectable` positions non-indexed with no normal attribute
 * (BridgeGeometryExporter emits three vertices per triangle so
 * `computeVertexNormals` yields flat faces), so face normals are derived here
 * from the winding rather than read off an attribute.
 *
 * Cost is bounded by the SELECTION, not the model: this runs over one
 * component's triangles when the selection changes, not over the merged bridge.
 * That is what keeps the string-keyed weld map affordable.
 *
 * A NOTE ON WINDING. The push follows the winding, so a component whose
 * triangles are not consistently wound (the exporter draws surfaces
 * `DoubleSide`, so nothing upstream has had to care) will push some faces inward
 * and show a dent in its contour. If that turns up, the fix is to orient each
 * face normal away from the component's centroid before accumulating — correct
 * for the convex-ish parts this draws (columns, girder segments, bearings) and
 * wrong for concave ones, which is why it is not done unconditionally.
 */
function smoothedNormals(positions: Float32Array): Float32Array {
  const vertCount = positions.length / 3;
  const out = new Float32Array(positions.length);

  // Accumulate every face normal onto each of its three welded positions.
  const acc = new Map<string, [number, number, number]>();
  const keys = new Array<string>(vertCount);
  const keyAt = (v: number): string => {
    const i = v * 3;
    return `${Math.round(positions[i] / WELD)},`
      + `${Math.round(positions[i + 1] / WELD)},`
      + `${Math.round(positions[i + 2] / WELD)}`;
  };
  for (let v = 0; v < vertCount; v++) keys[v] = keyAt(v);

  // Face normals, kept so a vertex whose welded accumulation cancels out can
  // fall back to the face it came from rather than to a zero the shader would
  // normalize into a NaN.
  const faceCount = Math.floor(vertCount / 3);
  const faceN = new Float32Array(faceCount * 3);
  const ax = new THREE.Vector3();
  const bx = new THREE.Vector3();
  const cx = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const n = new THREE.Vector3();

  for (let f = 0; f < faceCount; f++) {
    const i = f * 9;
    ax.set(positions[i], positions[i + 1], positions[i + 2]);
    bx.set(positions[i + 3], positions[i + 4], positions[i + 5]);
    cx.set(positions[i + 6], positions[i + 7], positions[i + 8]);
    ab.subVectors(bx, ax);
    ac.subVectors(cx, ax);
    n.crossVectors(ab, ac);
    // Area-weighted on purpose: the cross product's length IS twice the
    // triangle's area, so a long thin sliver (the exporter's fillet strips are
    // full of them) does not out-vote the broad face it sits beside.
    faceN[f * 3] = n.x;
    faceN[f * 3 + 1] = n.y;
    faceN[f * 3 + 2] = n.z;

    for (let k = 0; k < 3; k++) {
      const key = keys[f * 3 + k];
      let a = acc.get(key);
      if (!a) acc.set(key, a = [0, 0, 0]);
      a[0] += n.x;
      a[1] += n.y;
      a[2] += n.z;
    }
  }

  for (let v = 0; v < vertCount; v++) {
    const a = acc.get(keys[v])!;
    let len = Math.hypot(a[0], a[1], a[2]);
    let x = a[0];
    let y = a[1];
    let z = a[2];
    if (len < 1e-12) {
      // Two faces exactly back to back average to nothing — use the owning
      // face's own normal instead.
      const f = Math.floor(v / 3) * 3;
      x = faceN[f];
      y = faceN[f + 1];
      z = faceN[f + 2];
      len = Math.hypot(x, y, z);
    }
    if (len < 1e-12) { out[v * 3] = 0; out[v * 3 + 1] = 1; out[v * 3 + 2] = 0; continue; }
    out[v * 3] = x / len;
    out[v * 3 + 1] = y / len;
    out[v * 3 + 2] = z / len;
  }

  return out;
}

/**
 * The default `project_vertex` chunk with the hull's push in the middle of it.
 *
 * `mat3(modelViewMatrix)` rather than `normalMatrix`: the same direction for
 * anything rotated and uniformly scaled (the hull is neither — BridgeScene's
 * positions are already world-space), and guaranteed to be present. three
 * declares `normalMatrix` in every vertex shader but only uploads it for
 * materials that were going to light something, and a MeshBasicMaterial is not
 * one of those.
 *
 * `hullDepth` is 1 under a perspective camera and 0 under an orthographic one.
 * `max(-mvPosition.z, 0.001)` because a vertex behind the camera has positive z
 * and would be pushed INWARD, turning the contour inside out on a component the
 * camera is standing inside — which a bridge viewport does get to, flying the
 * camera through a box girder.
 *
 * `<clipping_planes_vertex>` runs after this and reads `mvPosition`, so the hull
 * is clipped by ClippingPanel's global planes at its own pushed position — the
 * contour gets cut off with the component instead of surviving the cut.
 */
const HULL_PROJECT = `
  vec4 mvPosition = vec4( transformed, 1.0 );
  mvPosition = modelViewMatrix * mvPosition;
  mvPosition.xyz += normalize( mat3( modelViewMatrix ) * hullNormal )
    * hullWidth * mix( 1.0, max( -mvPosition.z, 0.001 ), hullDepth );
  gl_Position = projectionMatrix * mvPosition;
`;

export interface SelectionHullOptions {
  /** Contour colour. */
  colour?: string;
  /** Multiplier on {@link LINE_PX}. */
  weight?: number;
}

/**
 * A per-component contour for the current selection.
 *
 * `set(parts)` once per selection change, `settle()` once a frame (the width is
 * a fraction of the distance to the camera, so it has to be restruck whenever
 * the camera moves or the contour would read as a shell that grows when the shot
 * pulls back).
 */
export class SelectionHull {
  private readonly viewport: Viewport3D;
  public readonly group = new THREE.Group();

  /** Shared by every hull, so the weight is one number rather than one per
   *  component that could disagree. */
  private readonly uWidth = { value: 0 };
  private readonly uDepth = { value: 1 };
  private readonly material: THREE.MeshBasicMaterial;

  /** Geometry built for the current selection — disposed on the next `set`. */
  private owned: THREE.BufferGeometry[] = [];

  private linePx = LINE_PX;
  private weight = 1;

  constructor(viewport: Viewport3D, opts: SelectionHullOptions = {}) {
    this.viewport = viewport;
    this.group.name = 'SelectionHull';
    this.weight = opts.weight ?? DEFAULT_WEIGHT;

    const mat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(opts.colour ?? '#ff8c1a'),
      side: THREE.BackSide,
      // The hull is a line, not a thing in the room: it must not hide the
      // component it outlines, and it must not write depth or the surfaces
      // drawn after it would test against a shell 2.5 px too big.
      depthWrite: false,
      depthTest: true,
    });
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.hullWidth = this.uWidth;
      shader.uniforms.hullDepth = this.uDepth;
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          '#include <common>\nuniform float hullWidth;\nuniform float hullDepth;\nattribute vec3 hullNormal;',
        )
        .replace('#include <project_vertex>', HULL_PROJECT);
    };
    // Without a cache key of its own a material with an `onBeforeCompile` is
    // keyed as if it had none, so it can be handed a plain MeshBasicMaterial's
    // program — the same shader without the push, i.e. an invisible contour.
    mat.customProgramCacheKey = () => 'bridge-selection-hull';
    this.material = mat;
  }

  /** Live contour colour. */
  public setColour(colour: string): void {
    this.material.color.set(colour);
  }

  /** Live contour weight (multiplier on the 2.5 px base). */
  public setWeight(weight: number): void {
    this.weight = Math.max(0.25, Math.min(4, weight));
  }

  /**
   * Rebuild the contour for one hull per entry.
   *
   * ONE HULL PER COMPONENT, not one merged hull for the selection — merging
   * would put the two touching halves of a bent under a single silhouette and
   * lose the line between them, which is the entire reason this is not
   * OutlinePass.
   *
   * Returns how many contours were actually built, which is not always
   * `parts.length` — a part carrying fewer than one triangle is skipped. Worth
   * having as a number rather than an assumption: "the outline is missing on
   * that pier" is otherwise indistinguishable from "that pier was never in the
   * list".
   */
  public set(parts: Array<{ id: string; positions: number[] }>): number {
    this.clear();
    if (!parts.length) return 0;

    let built = 0;
    for (const part of parts) {
      if (part.positions.length < 9) continue;
      const positions = new Float32Array(part.positions);
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geom.setAttribute('hullNormal', new THREE.BufferAttribute(smoothedNormals(positions), 3));
      this.owned.push(geom);

      const mesh = new THREE.Mesh(geom, this.material);
      mesh.name = `Hull:${part.id}`;
      // Nothing about a contour belongs in the shadow map: a hull that cast one
      // would print a silhouette of the component a fraction too large on the
      // deck beside it.
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      // Positions are already world-space (BridgeScene uploads exporter
      // coordinates verbatim), so the mesh stays at the origin.
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrixWorld(true);
      this.group.add(mesh);
      built++;
    }
    return built;
  }

  /**
   * Sort the contour against a model that may be see-through.
   *
   * An inverted hull behind an OPAQUE surface has all of itself hidden except
   * the sliver round the silhouette, and that sliver is the line. Behind a
   * TRANSPARENT one nothing hides it and the whole back-face shell shows through
   * as a filled shape — a selected girder would read as a solid orange lump.
   * Ordered after the transparent pass and depth-tested against it, the hull's
   * back faces fail the test wherever the component's own front face is nearer
   * and pass only where they poke out past it, which is the line again.
   *
   * `transparent` here is not about opacity — it is how a material asks to be
   * sorted into the pass that runs after the opaque one, which is where it has
   * to be to test against a ghosted deck. The line itself stays full strength.
   */
  public setModelTransparent(transparent: boolean): void {
    if (this.material.transparent === transparent) return;
    this.material.transparent = transparent;
    this.material.opacity = 1;
    this.material.needsUpdate = true;
    for (const child of this.group.children) child.renderOrder = transparent ? 10 : 0;
  }

  /**
   * This frame's width, in pixels held constant across camera moves.
   *
   * Perspective: the push is a fraction of the distance to the camera, so the
   * pixels it comes out as are that fraction times the focal length in pixels,
   * `(height / 2) / tan(fov / 2)`. Turning that round gives the fraction a
   * `linePx` contour needs, which is why the weight can be quoted in pixels and
   * hold at any viewport size and any field of view.
   *
   * Orthographic: on-screen size is governed by the frustum height and zoom
   * rather than by depth, so the push is a flat world distance and `hullDepth`
   * drops the depth term out of the shader.
   */
  public settle(): void {
    if (!this.group.children.length) return;
    const cam = this.viewport.camera;
    const size = this.viewport.renderer.getSize(_size);
    const height = Math.max(1, size.y);
    const px = this.linePx * this.weight;

    if (cam instanceof THREE.PerspectiveCamera) {
      const focal = (height / 2) / Math.tan((cam.fov * Math.PI) / 360);
      this.uWidth.value = px / focal;
      this.uDepth.value = 1;
    } else {
      const worldPerPx = ((cam.top - cam.bottom) / cam.zoom) / height;
      this.uWidth.value = px * worldPerPx;
      this.uDepth.value = 0;
    }
  }

  /** Drop the current contour's geometry. The material is reused. */
  public clear(): void {
    for (const child of [...this.group.children]) this.group.remove(child);
    for (const g of this.owned) g.dispose();
    this.owned.length = 0;
  }

  /** Free everything, including the shared material. */
  public dispose(): void {
    this.clear();
    this.material.dispose();
  }
}

const _size = new THREE.Vector2();
